import { pageTitle, type GameSummary } from '@cardengine/shared';
import { useEffect, useRef, useState } from 'react';
import { linkProps } from '../router.js';
import { fetchBoard, submitScore, type CabinetApi } from './scores.js';

/** How a cabinet's code is loaded. Each is its own chunk. */
type Loader = () => Promise<{ start: (canvas: HTMLCanvasElement, api: CabinetApi) => () => void }>;

/*
 * Loaded on demand rather than bundled with the site.
 *
 * Each cabinet is a few hundred lines of game that most visitors will never
 * open, and the card games should not pay for them on first load. Dynamic
 * imports put each in its own chunk, fetched when someone actually walks up to
 * the machine.
 */
const CABINETS: Record<string, Loader> = {
  'last-line': () => import('./games/lastLine.js'),
  circuit: () => import('./games/circuit.js'),
  shard: () => import('./games/shard.js'),
  stack: () => import('./games/stack.js'),
  descent: () => import('./games/descent.js'),
};

export function isCabinet(gameId: string): boolean {
  return gameId in CABINETS;
}

/**
 * Mounts an arcade cabinet.
 *
 * The board is fetched *before* the game starts, so the game's own boot
 * sequence — which reads its high scores synchronously, as it always did —
 * needs no rewriting to become asynchronous.
 */
export function Cabinet({ game }: { game: GameSummary }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [status, setStatus] = useState<'loading' | 'running' | 'failed'>('loading');

  useEffect(() => {
    const load = CABINETS[game.id];
    const canvas = canvasRef.current;
    if (!load || !canvas) return;

    let stop: (() => void) | null = null;
    // Guards the two awaits below: a route change before either resolves must
    // not start a game onto a canvas that has already left the page.
    let cancelled = false;

    void (async () => {
      try {
        const [mod, board] = await Promise.all([load(), fetchBoard(game.id)]);
        if (cancelled) return;
        stop = mod.start(canvas, {
          board,
          submit: (entry) => submitScore(game.id, entry),
        });
        setStatus('running');
      } catch {
        if (!cancelled) setStatus('failed');
      }
    })();

    return () => {
      cancelled = true;
      stop?.();
    };
  }, [game.id]);

  useEffect(() => {
    document.title = pageTitle(game.name);
  }, [game.name]);

  /*
   * Stops the browser acting on the keys the game is using.
   *
   * The cabinets guard the arrows only while you are entering your initials,
   * because as full-screen pages they had an `overflow: hidden` body and
   * nothing to scroll. Here they sit in a site that does scroll, so steering
   * with the arrows — or firing with space — would walk the page down and out
   * from under the game. Held here rather than patched into the vendored
   * source: it is the page's problem, and the page is what changed.
   */
  useEffect(() => {
    const swallow = new Set(['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', ' ']);
    const block = (event: KeyboardEvent) => {
      // Modified presses still belong to the browser — ctrl+arrow and the like.
      if (event.ctrlKey || event.metaKey || event.altKey) return;
      if (swallow.has(event.key)) event.preventDefault();
    };
    window.addEventListener('keydown', block);
    document.body.classList.add('has-cabinet');
    return () => {
      window.removeEventListener('keydown', block);
      document.body.classList.remove('has-cabinet');
    };
  }, []);

  return (
    <main className="cabinet">
      <div className="cabinet__screen">
        <canvas ref={canvasRef} className="cabinet__canvas" />
        {status === 'loading' && <p className="cabinet__note">Warming up the tube…</p>}
        {status === 'failed' && (
          <p className="cabinet__note">
            This machine is out of order. Give it a moment and reload.
          </p>
        )}
      </div>
      <nav className="cabinet__bar">
        <a className="btn btn--ghost btn--small" {...linkProps('/')}>
          Back to the bar
        </a>
        <span className="cabinet__name">{game.name}</span>
        <a className="btn btn--ghost btn--small" {...linkProps(`${game.href}/how-to-play`)}>
          How to play
        </a>
      </nav>
    </main>
  );
}
